import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth, useSignIn } from '@clerk/clerk-react';

/**
 * MobileEntry Component
 *
 * Handles mobile app bootstrap flow:
 * 1. Receives Firebase ID token from mobile app headers
 * 2. Exchanges it for Clerk session via backend
 * 3. Redirects to target page within the web app
 *
 * Query params:
 *   - target: target path (e.g., /client, /calendar)
 *   - webview: true/false (mobile webview flag)
 *   - showmenu: true/false (whether to show menu)
 *   - nativeheader: true/false (render header in React Native app)
 */
const MobileEntry = () => {
  const history = useHistory();
  const location = useLocation();
  const { isLoaded, isSignedIn } = useAuth();
  const { signIn, setActive } = useSignIn();
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(true);

  const extractErrorMessage = (err, fallbackMessage) => {
    if (!err) {
      return fallbackMessage;
    }

    if (Array.isArray(err.errors) && err.errors.length > 0) {
      const primaryError = err.errors[0];
      return (
        primaryError?.longMessage ||
        primaryError?.message ||
        primaryError?.code ||
        fallbackMessage
      );
    }

    return err.message || fallbackMessage;
  };

  const sanitizeTargetPath = (rawTarget) => {
    const allowedTargets = [
      '/client',
      '/calendar',
      '/google-business',
      '/images',
      '/rank-tracker',
      '/reports',
      '/seo-audit'
    ];

    if (!rawTarget || typeof rawTarget !== 'string') {
      return '/client';
    }

    if (!rawTarget.startsWith('/') || rawTarget.startsWith('//')) {
      return '/client';
    }

    try {
      const parsed = new URL(rawTarget, window.location.origin);
      return allowedTargets.includes(parsed.pathname)
        ? `${parsed.pathname}${parsed.search}${parsed.hash}`
        : '/client';
    } catch (err) {
      return '/client';
    }
  };

  const parseBooleanParam = (value, defaultValue = false) => {
    if (value === null || value === undefined) {
      return defaultValue;
    }

    const normalized = String(value).trim().toLowerCase();

    if (['1', 'true', 'yes'].includes(normalized)) {
      return true;
    }

    if (['0', 'false', 'no'].includes(normalized)) {
      return false;
    }

    return defaultValue;
  };

  const buildMobileSearch = ({ webview, showmenu, nativeheader }) => {
    const params = new URLSearchParams();
    params.set('webview', String(Boolean(webview)));
    params.set('showmenu', String(Boolean(showmenu)));
    params.set('nativeheader', String(Boolean(nativeheader)));
    return `?${params.toString()}`;
  };

  const isWebviewMode = parseBooleanParam(
    new URLSearchParams(location.search).get('webview'),
    false
  );

  useEffect(() => {
    const performBootstrap = async () => {
      try {
        const queryParams = new URLSearchParams(location.search);
        const target = sanitizeTargetPath(
          queryParams.get('target') || '/client'
        );
        const webview = parseBooleanParam(queryParams.get('webview'), false);
        const showmenu = parseBooleanParam(queryParams.get('showmenu'), true);
        const nativeheader = parseBooleanParam(
          queryParams.get('nativeheader'),
          webview
        );
        const signInTicket = queryParams.get('ticket');

        console.log('[MobileEntry] bootstrap params', {
          target,
          webview,
          showmenu,
          nativeheader,
          hasTicket: Boolean(signInTicket),
          ticketLength: signInTicket ? signInTicket.length : 0
        });

        // If already signed in via Clerk, just redirect
        if (isLoaded && isSignedIn) {
          history.replace({
            pathname: target,
            search: buildMobileSearch({ webview, showmenu, nativeheader })
          });
          return;
        }

        // Wait for Clerk to load
        if (!isLoaded) {
          return;
        }

        if (signInTicket) {
          if (!signIn) {
            setError('Clerk is not ready to sign in');
            setIsProcessing(false);
            return;
          }

          const signInAttempt = await signIn.create({
            strategy: 'ticket',
            ticket: signInTicket.trim()
          });

          if (signInAttempt.status === 'complete') {
            await setActive({ session: signInAttempt.createdSessionId });
            history.replace({
              pathname: target,
              search: buildMobileSearch({ webview, showmenu, nativeheader })
            });
            return;
          }

          setError('Could not complete Clerk session (ticket not completed)');
          setIsProcessing(false);
          return;
        }

        // Call backend bootstrap endpoint
        // Backend expects headers set by mobile app:
        // - Authorization: Bearer <firebase_id_token>
        // - X-Firebase-UID: <uid>
        // - X-Mobile-Session-Bootstrap: 1
        // - X-Mobile-Target: <target>
        const bootstrapResponse = await fetch('/api/mobile-bootstrap', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            target,
            webview,
            showmenu,
            nativeheader
          }),
          credentials: 'include' // Important: include cookies
        });

        if (
          bootstrapResponse.status === 401 ||
          bootstrapResponse.status === 403
        ) {
          setError('Authentication failed. Please try again.');
          setIsProcessing(false);
          return;
        }

        if (!bootstrapResponse.ok) {
          const errorData = await bootstrapResponse.json();
          setError(errorData.message || 'Bootstrap failed');
          setIsProcessing(false);
          return;
        }

        const data = await bootstrapResponse.json();

        if (!data?.signInToken) {
          setError('Bootstrap without a valid signInToken');
          setIsProcessing(false);
          return;
        }

        if (!signIn) {
          setError('Clerk is not ready to sign in');
          setIsProcessing(false);
          return;
        }

        const signInAttempt = await signIn.create({
          strategy: 'ticket',
          ticket: String(data.signInToken).trim()
        });

        if (signInAttempt.status === 'complete') {
          await setActive({ session: signInAttempt.createdSessionId });
          history.replace({
            pathname: data.target || target,
            search: buildMobileSearch({ webview, showmenu, nativeheader })
          });
          return;
        }

        setError('Could not complete Clerk session (fallback)');
        setIsProcessing(false);
        return;
      } catch (err) {
        console.error('Mobile bootstrap error:', err);
        setError(
          extractErrorMessage(err, 'An error occurred during bootstrap')
        );
        setIsProcessing(false);
      }
    };

    performBootstrap();
  }, [isLoaded, isSignedIn, location.search, history, signIn, setActive]);

  if (isProcessing) {
    if (isWebviewMode) {
      return <div className="min-h-screen bg-background" />;
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">
            Initializing session...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 max-w-sm text-center">
          <div className="text-lg font-semibold text-destructive">
            Bootstrap Failed
          </div>
          <p className="text-sm text-muted-foreground">{error}</p>
          <button
            onClick={() => (window.location.href = '/sign-in')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <p className="text-muted-foreground">Redirecting...</p>
    </div>
  );
};

export default MobileEntry;
